/**
 * CDN bundle entry. Build with: npm run build:embed
 * Exposes window.DiscoveryPlpEmbed with mount() and setPageData() for use on any page.
 */
import { createApp } from 'vue';
import { createStore } from 'vuex';
import { createRouter, createMemoryHistory } from 'vue-router';
import PlpEmbed from './PlpEmbed.vue';
import storeModule from '@store';
import { config as embedConfig } from './embed-config.js';

let mountedInstance = null;

/**
 * Resolve the API domain to use for Discover API calls.
 * Returns 'www.macys.com' when running on localhost, a file path, or storage.googleapis.com
 * (i.e. dev/demo contexts). On any other host, uses window.location.host so the embed
 * works on staging or partner domains that proxy the Discover API.
 * @returns {string}
 */
function resolveDomain() {
  const host = (typeof window !== 'undefined' && window.location.host) || '';
  return (
    host.indexOf('macys.com') > -1 ||
    host.indexOf('localhost') > -1 ||
    host === '' ||
    host.indexOf('storage.googleapis.com') > -1
  ) ? 'www.macys.com' : host;
}

/**
 * If url is a relative path (starts with '/'), prepend the resolved domain.
 * If url is already absolute, return as-is.
 * @param {string} url
 * @returns {string}
 */
function resolveUrl(url) {
  if (!url) return url;

  let pathWithQuery = null;
  if (/^https?:\/\/localhost(:\d+)?/.test(url)) {
    pathWithQuery = url.replace(/^https?:\/\/localhost(:\d+)?/, '');
  } else if (url.startsWith('/') && /[?&]id=/.test(url)) {
    pathWithQuery = url;
  }

  if (pathWithQuery !== null) {
    const pathname = pathWithQuery.replace('?id=', '&id=');
    const idMatch = pathname.match(/[?&]id=([^&/?]+)/);
    const categoryId = idMatch ? idMatch[1] : '';
    return `https://www.macys.com/xapi/discover/v1/page?id=${categoryId}&pathname=${pathname}`;
  }

  if (url.startsWith('http')) return url;
  return 'https://' + resolveDomain() + url;
}

/**
 * Fetch page data, following a JSON 301 redirect if present.
 * When the response is {"redirect":{"url":"/shop/foo?id=123","httpCode":"301"}},
 * re-fetches from that URL with ?id= replaced by &id= (Macy's canonical URL format).
 * @param {string} url
 * @returns {Promise<Object>}
 */
async function fetchPageData(url) {
  const res = await fetch(resolveUrl(url), { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data.redirect?.url) {
    console.log(data.redirect.url, 'redirecting');
    const redirectUrl = data.redirect.url.replace('?id=', '&id=');
    const res2 = await fetch(resolveUrl(redirectUrl), { credentials: 'include' });
    if (!res2.ok) throw new Error(`HTTP ${res2.status}`);
    return res2.json();
  }
  return data;
}

/** Minimal router with in-memory history so route/query (e.g. id) don't change the host page URL. */
function createEmbedRouter() {
  const router = createRouter({
    history: createMemoryHistory(),
    routes: [{ path: '/:pathMatch(.*)*', name: 'embed', component: { template: '<div></div>' } }],
  });
  return router;
}

/**
 * Mount the PLP embed on the page.
 * @param {string|Element} selector - CSS selector or DOM element to mount into (e.g. '#plp-embed').
 * @param {Object} [options]
 * @param {string} [options.brand='mcom'] - 'mcom' or 'bcom'.
 * @param {Object} [options.pageData] - Pre-loaded page payload (body.canvas.rows[].rowSortableGrid). If omitted, you must call setPageData() before or after mount.
 * @param {string} [options.apiUrl] - If provided, page data is fetched from this URL before mounting. Overrides pageData.
 * @param {string|number} [options.categoryId] - Category ID for filtering/xAPI. If omitted, the embed uses meta.analytics.data.categoryID from page data.
 * @returns {Promise<{ app: import('vue').App, store: import('vuex').Store }>}
 */
async function mount(selector, options = {}) {
  if (options.brand) {
    embedConfig.brand = options.brand;
  }

  const store = createStore(storeModule);

  // Embed has no server env; provide envProps so useEnvParams() does not return undefined
  const brand = options.brand || embedConfig.brand || 'mcom';
  store.state.envProps = {
    brand,
    isBcom: brand === 'bcom',
    isMcom: brand === 'mcom',
    assetHost: '',
    navigationAssetHost: '',
    proxyProsHost: '',
    navigationHostLink: '',
    tagMngrEnabled: false,
    tagMngrAccount: '',
    tagMngrEnv: 'prod',
    tealiumSyncEnabled: false,
    xMacysApiKey: '',
    hideColorFacetPanelEnabled: false,
    enableAsyncAnalyticsTags: false,
  };

  if (options.apiUrl) {
    try {
      const data = await fetchPageData(options.apiUrl);
      store.commit('SET_PAGE', { ...data, redirect: null, pagination: data.pagination });
    } catch (err) {
      console.error('PLP Embed: failed to fetch page data', err);
      throw err;
    }
  } else if (options.pageData) {
    store.commit('SET_PAGE', options.pageData);
  }

  const el = typeof selector === 'string' ? document.querySelector(selector) : selector;
  if (!el) {
    throw new Error(`PLP Embed: mount target not found: ${selector}`);
  }

  const router = createEmbedRouter();
  const categoryId = options.categoryId ?? store.getters.getAnalyticsData?.categoryID;
  if (categoryId != null) {
    router.replace({ path: '/', query: { id: String(categoryId), CategoryID: String(categoryId) } });
  }
  const app = createApp(PlpEmbed);
  app.provide('embedCategoryId', categoryId != null ? String(categoryId) : undefined);
  app.use(store);
  app.use(router);
  app.mount(el);
  mountedInstance = { app, store };
  return mountedInstance;
}

/**
 * Set page data on the store after mount (e.g. after fetching from your API).
 * Must be called after mount() with the store it returned, or the store you passed to mount.
 * @param {import('vuex').Store} store - Store instance returned from mount().
 * @param {Object} pageData - Full page payload for SET_PAGE (body.canvas.rows, meta, etc.).
 */
function setPageData(store, pageData) {
  if (!store) return;
  store.commit('SET_PAGE', pageData);
}

/**
 * Fetch a new product pool from a URL and update the embed store.
 * Useful for wiring up elements with a data-pool attribute.
 * @param {string} url - xAPI URL (e.g. the value of a data-pool attribute).
 * @returns {Promise<void>}
 */
async function loadPool(url) {
  if (!mountedInstance?.store) {
    throw new Error('PLP Embed: loadPool() called before mount()');
  }
  const data = await fetchPageData(url);
  mountedInstance.store.commit('SET_PAGE', { ...data, redirect: null, pagination: data.pagination });
}

function unmount() {
  if (mountedInstance?.app) {
    mountedInstance.app.unmount();
    mountedInstance = null;
  }
}

const DiscoveryPlpEmbed = {
  mount,
  setPageData,
  loadPool,
  unmount,
};

export { DiscoveryPlpEmbed, mount, setPageData, loadPool, unmount };

if (typeof window !== 'undefined') {
  window.DiscoveryPlpEmbed = DiscoveryPlpEmbed;
}
